import { Button, OverflowTooltip, Table, useFetch } from "@4uhub/lib4uhub";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { format, isValid, parse, parseISO } from "date-fns";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { IESignRuleDocumentList } from "../../../../../../models/esign-rule";
import MainApi from "../../../../../../services/mainApi.service";
import { getESignRuleById } from "../../../../../../services/esignRule.service";
import { DocumentsTab } from "../BasicData/DocumentsTab";
import { getModelFilesForAutomationCode } from "../BasicData/modelFiles";
import { IDocumentsPage } from "./models";

const translationPath = "4uESign.page.automations.automationRules.";

const _4UESIGN_DOCUMENT_ROUTE =
  process.env.REACT_APP_4UESIGN + "/api/v1/ESignRuleDocument/";

const service = new MainApi<IESignRuleDocumentList>(_4UESIGN_DOCUMENT_ROUTE);

const formatDocumentDate = (value: unknown) => {
  if (!value) return "";

  const parsedDate =
    value instanceof Date
      ? value
      : typeof value === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(value)
        ? parse(value, "MM/dd/yyyy", new Date())
        : typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)
          ? parseISO(value)
          : typeof value === "string"
            ? new Date(value)
            : null;

  if (!parsedDate) return "";

  return isValid(parsedDate) ? format(parsedDate, "dd/MM/yyyy") : "";
};

export const DocumentsPage = memo(({ esignAutomationCode }: IDocumentsPage) => {
  const { id: ruleId } = useParams();
  const { t } = useTranslation();

  const [showForm, setShowForm] = useState(false);
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [automationCode, setAutomationCode] = useState<string | undefined>();

  const { sendRequest: getRuleById } = useFetch(getESignRuleById);

  const columns: GridColDef<IESignRuleDocumentList>[] = [
    {
      field: "name",
      headerName: t(translationPath + "name") || "Name",
      flex: 1,
      renderCell: (params) => (
        <OverflowTooltip
          text={params.value}
          tooltip={params.value}
          textTransform="none"
          variant="body2"
        />
      ),
    },
    {
      field: "order",
      headerName: t(translationPath + "order") || "Order",
      width: 100,
    },
    {
      field: "initialDate",
      headerName: t(translationPath + "initial_date") || "Start Date",
      width: 150,
      valueFormatter: (params) => formatDocumentDate(params.value),
      renderCell: (params) => (
        <OverflowTooltip
          text={String(params.formattedValue ?? "")}
          tooltip={String(params.formattedValue ?? "")}
          textTransform="none"
          variant="body2"
        />
      ),
    },
    {
      field: "endDate",
      headerName: t(translationPath + "end_date") || "End Date",
      width: 150,
      valueFormatter: (params) => formatDocumentDate(params.value),
      renderCell: (params) => (
        <OverflowTooltip
          text={String(params.formattedValue ?? "")}
          tooltip={String(params.formattedValue ?? "")}
          textTransform="none"
          variant="body2"
        />
      ),
    },
    {
      field: "enable",
      headerName: t(translationPath + "status") || "Status",
      width: 120,
      renderCell: (params) => (
        <Chip
          size="small"
          label={
            params.value
              ? t(translationPath + "active") || "Active"
              : t(translationPath + "inactive") || "Inactive"
          }
          color={params.value ? "success" : "error"}
        />
      ),
    },
  ];

  const handleAdd = () => {
    setSelectedId(undefined);
    setShowForm(true);
  };

  const handleRowClick = (params: GridRowParams<IESignRuleDocumentList>) => {
    if (!params.row) return;
    setSelectedId(params.row.id);
    setShowForm(true);
  };

  const handleBack = () => {
    setSelectedId(undefined);
    setShowForm(false);
  };

  useEffect(() => {
    const fetchAutomationCode = async () => {
      if (!ruleId) return;

      const response = await getRuleById(ruleId);

      if (
        response.success &&
        response.data?.esignAutomation?.code !== undefined
      ) {
        setAutomationCode(response.data.esignAutomation.code.toString());
        return;
      }

      setAutomationCode(undefined);
    };

    fetchAutomationCode();
  }, [getRuleById, ruleId]);

  const modelFiles = useMemo(
    () => getModelFilesForAutomationCode(automationCode),
    [automationCode],
  );

  const handleModelFileDownload = useCallback(
    (href: string, downloadName: string) => {
      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", downloadName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    [],
  );

  const params = useMemo(() => {
    return {
      RuleId: ruleId,
    };
  }, [ruleId]);

  if (!ruleId) return null;

  return (
    <Box sx={{ height: 600, width: "100%" }}>
      {showForm ? (
        <Box
          sx={(theme) => ({
            p: 1,
            height: "100%",
            overflow: "auto",
            backgroundColor: theme.palette.background.default,
            borderRadius: 1,
          })}
        >
          <DocumentsTab
            id={selectedId}
            ruleId={ruleId}
            esignAutomationCode={esignAutomationCode}
            onBack={handleBack}
          />
        </Box>
      ) : (
        <Box height={600}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Typography variant="h5">
              {t(translationPath + "documents") || "Documents"}
            </Typography>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="flex-end"
              flexWrap="wrap"
              useFlexGap
            >
              {modelFiles.map((file) => (
                <Button
                  key={file.href}
                  variant="outlined"
                  color="inherit"
                  startIcon={<DownloadRoundedIcon />}
                  onClick={() =>
                    handleModelFileDownload(file.href, file.downloadName)
                  }
                >
                  {t(translationPath + "download_model")}
                </Button>
              ))}

              <Button variant="contained" onClick={handleAdd}>
                {t(translationPath + "add_document") || "Add Document"}
              </Button>
            </Stack>
          </Stack>

          <Table<IESignRuleDocumentList>
            showDefaultMacroFilters={false}
            service={service}
            columns={columns}
            title={null}
            searchInput
            onRowClick={handleRowClick}
            serviceProps={params}
            defaultPageSize={20}
            pageSizeOptions={[5, 10, 20]}
            searchInputPlaceHolder={
              t(translationPath + "document_search") || "Search Document..."
            }
            showButton={false}
            confirmOptions={{
              title:
                t(translationPath + "document_delete.title") ||
                "Confirm Deletion",
              description:
                t(translationPath + "document_delete.description") ||
                "Are you sure you want to delete this document?",
              confirmationButtonProps: {
                variant: "contained",
                color: "error",
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
});
